"use strict";

exports.__esModule = true;
exports.NotificationsIcon = void 0;
var _react = _interopRequireWildcard(require("react"));
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _reactIntl = require("react-intl");
var _core = require("@blueprintjs/core");
var _icons = require("@blueprintjs/icons");
var _momentTimezone = _interopRequireDefault(require("moment-timezone"));
var _polished = require("polished");
var _Theme = require("../../Theme");
var _Core = require("../../Core");
var _Time = require("../../Time");
var _NotificationsIconContext = require("./NotificationsIconContext");
var _NotificationPopupWidget = require("./NotificationPopupWidget");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function _interopRequireWildcard(e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, "default": e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (var _t in e) "default" !== _t && {}.hasOwnProperty.call(e, _t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, _t)) && (i.get || i.set) ? o(f, _t, i) : f[_t] = e[_t]); return f; })(e, t); }
// The messages panel in the modern UI's own language. A white heading holds
// the title, a segmented All / Unread filter on a soft track and a pill
// button that marks everything read. Under it, on the canvas grey of a
// dashboard, every message is a small shadow card: a left edge in its type's
// colour, a type chip, the title, an orange dot while unread, two lines of
// the message and the date in mono. Same context, same detail dialog and the
// same test ids as the legacy panel. The envelope keeps the navbar's quiet
// square look and carries its own count pill.
var PANEL_WIDTH = 380;
var LIST_MAX_HEIGHT = 460;
var DATE_FORMAT = "YYYY-MM-DD";
var FILTER_ALL = "all";
var FILTER_UNREAD = "unread";
var NEUTRAL_EDGE = "#C7CFD5";

// A message's colour: the modern blue for information, the modern amber for
// a warning, neutral for anything else.
var typeColor = function typeColor(theme, type) {
  switch (type) {
    case "warning":
      return theme.PlannedDowntimeStateColor || "#F0A94B";
    case "info":
      return theme.ChartStrokeColor || "#3A86C8";
    default:
      return NEUTRAL_EDGE;
  }
};
var typeIcon = function typeIcon(type) {
  switch (type) {
    case "warning":
      return _icons.IconNames.WARNING_SIGN;
    case "info":
      return _icons.IconNames.INFO_SIGN;
    default:
      return _icons.IconNames.ENVELOPE;
  }
};
var Anchor = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__Anchor",
  componentId: "sc-1y0c5re-0"
})(["position:relative;margin-right:10px;"]);
var Badge = _styledComponents["default"].span.withConfig({
  displayName: "NotificationsIconmodern__Badge",
  componentId: "sc-1y0c5re-1"
})(["position:absolute;top:-6px;right:-6px;box-sizing:border-box;min-width:18px;height:18px;padding:0 5px;border-radius:9px;border:2px solid ", ";background:", ";color:#ffffff;font-family:", ";font-size:10px;font-weight:600;line-height:14px;text-align:center;pointer-events:none;"], (0, _Theme.val)("NavbarBackground"), (0, _Theme.val)("bp6-intent-primary"), (0, _Theme.val)("ChartFontFamilyMono"));
var Panel = _styledComponents["default"].aside.withConfig({
  displayName: "NotificationsIconmodern__Panel",
  componentId: "sc-1y0c5re-2"
})(["display:flex;flex-direction:column;width:", "px;max-width:calc(100vw - 24px);overflow:hidden;border-radius:10px;background:", ";color:", ";"], PANEL_WIDTH, (0, _Theme.val)("bp6-app-background-color"), (0, _Theme.val)("color"));

// The heading floats over the canvas on a soft shadow rather than a rule.
var Header = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__Header",
  componentId: "sc-1y0c5re-3"
})(["flex:none;position:relative;z-index:1;padding:14px 14px 12px 16px;background:#ffffff;box-shadow:0 1px 2px rgba(29,36,41,0.06),0 4px 10px -6px rgba(29,36,41,0.12);"]);
var TitleRow = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__TitleRow",
  componentId: "sc-1y0c5re-4"
})(["display:flex;align-items:center;gap:10px;margin-bottom:10px;"]);
var Title = _styledComponents["default"].h3.withConfig({
  displayName: "NotificationsIconmodern__Title",
  componentId: "sc-1y0c5re-5"
})(["margin:0;font-family:", ";font-size:17px;font-weight:600;letter-spacing:-0.01em;color:", ";"], (0, _Theme.val)("FontFamilyHeading"), (0, _Theme.val)("color"));
var ReadAll = _styledComponents["default"].button.withConfig({
  displayName: "NotificationsIconmodern__ReadAll",
  componentId: "sc-1y0c5re-6"
})(["margin-left:auto;height:26px;padding:0 10px;border:0;border-radius:8px;background:#ffffff;box-shadow:", ";font:inherit;font-size:12px;font-weight:600;color:", ";white-space:nowrap;cursor:pointer;&:hover{box-shadow:", ";}&:disabled{background:", ";box-shadow:none;color:", ";cursor:default;}"], (0, _Theme.val)("ButtonShadow"), (0, _Theme.val)("color"), (0, _Theme.val)("ButtonShadowHover"), (0, _Theme.val)("ChartCardChipBackground"), (0, _Theme.val)("mutedColor"));

// The segmented control from the modern toolbars: a soft track, the chosen
// segment a white tab on a hairline shadow.
var Segments = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__Segments",
  componentId: "sc-1y0c5re-7"
})(["display:inline-flex;gap:2px;padding:3px;border-radius:8px;border:1px solid ", ";background:", ";"], (0, _Theme.val)("toolbarBorderColor"), (0, _Theme.val)("bp6-app-background-color"));
var Segment = _styledComponents["default"].button.withConfig({
  displayName: "NotificationsIconmodern__Segment",
  componentId: "sc-1y0c5re-8"
})(["display:inline-flex;align-items:center;gap:6px;min-height:24px;padding:0 10px;border:0;border-radius:6px;background:", ";box-shadow:", ";font:inherit;font-size:12px;font-weight:600;color:", ";cursor:pointer;"], function (props) {
  return props.$active ? "#ffffff" : "transparent";
}, function (props) {
  return props.$active ? "0 1px 2px rgba(29, 36, 41, 0.08)" : "none";
}, function (props) {
  return props.$active ? (0, _Theme.val)("color")(props) : (0, _Theme.val)("ChartCardChipColor")(props);
});
var SegmentCount = _styledComponents["default"].span.withConfig({
  displayName: "NotificationsIconmodern__SegmentCount",
  componentId: "sc-1y0c5re-9"
})(["font-family:", ";font-size:10.5px;font-weight:600;opacity:0.7;"], (0, _Theme.val)("ChartFontFamilyMono"));
var List = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__List",
  componentId: "sc-1y0c5re-10"
})(["display:flex;flex-direction:column;gap:8px;max-height:", "px;overflow-y:auto;padding:10px;"], LIST_MAX_HEIGHT);

// A card must never shrink to fit the list's height: with its overflow
// hidden a flex item may, and 47 messages would squash into 460px instead of
// scrolling.
var Card = _styledComponents["default"].section.withConfig({
  displayName: "NotificationsIconmodern__Card",
  componentId: "sc-1y0c5re-11"
})(["flex:none;position:relative;overflow:hidden;padding:10px 12px 10px 16px;border-radius:10px;background:#ffffff;box-shadow:", ";cursor:pointer;transition:box-shadow 0.15s ease,transform 0.15s ease;&:hover{box-shadow:", ";transform:translateY(-1px);}"], (0, _Theme.val)("CardShadow"), (0, _Theme.val)("CardShadowRaised"));
var Edge = _styledComponents["default"].span.withConfig({
  displayName: "NotificationsIconmodern__Edge",
  componentId: "sc-1y0c5re-12"
})(["position:absolute;top:0;bottom:0;left:0;width:3px;background:", ";opacity:", ";"], function (props) {
  return typeColor(props.theme, props.$type);
}, function (props) {
  return props.$unread ? 1 : 0.45;
});
var TopLine = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__TopLine",
  componentId: "sc-1y0c5re-13"
})(["display:flex;align-items:center;gap:8px;min-width:0;"]);
var TypeChip = _styledComponents["default"].span.withConfig({
  displayName: "NotificationsIconmodern__TypeChip",
  componentId: "sc-1y0c5re-14"
})(["flex:none;display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:6px;background:", ";color:", ";"], function (props) {
  return (0, _polished.rgba)(typeColor(props.theme, props.$type), 0.14);
}, function (props) {
  return typeColor(props.theme, props.$type);
});
var CardTitle = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__CardTitle",
  componentId: "sc-1y0c5re-15"
})(["flex:1 1 auto;min-width:0;font-size:12.5px;line-height:18px;font-weight:", ";color:", ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"], function (props) {
  return props.$unread ? 600 : 500;
}, function (props) {
  return props.$unread ? (0, _Theme.val)("color")(props) : "#3D4A53";
});
var Dot = _styledComponents["default"].span.withConfig({
  displayName: "NotificationsIconmodern__Dot",
  componentId: "sc-1y0c5re-16"
})(["flex:none;width:8px;height:8px;border-radius:50%;background:", ";"], (0, _Theme.val)("bp6-intent-primary"));
var CardDate = _styledComponents["default"].time.withConfig({
  displayName: "NotificationsIconmodern__CardDate",
  componentId: "sc-1y0c5re-17"
})(["flex:none;font-family:", ";font-size:10.5px;color:", ";white-space:nowrap;"], (0, _Theme.val)("ChartFontFamilyMono"), (0, _Theme.val)("ChartAxisTextColor"));
var CardMessage = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__CardMessage",
  componentId: "sc-1y0c5re-18"
})(["margin:4px 0 0 28px;font-size:12px;line-height:16px;color:", ";display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;"], (0, _Theme.val)("mutedColor"));
var Empty = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__Empty",
  componentId: "sc-1y0c5re-19"
})(["padding:34px 16px 38px;text-align:center;"]);
var EmptyIcon = _styledComponents["default"].span.withConfig({
  displayName: "NotificationsIconmodern__EmptyIcon",
  componentId: "sc-1y0c5re-20"
})(["display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;margin-bottom:10px;border-radius:50%;background:#ffffff;box-shadow:", ";color:#9fadb6;"], (0, _Theme.val)("CardShadow"));
var EmptyTitle = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__EmptyTitle",
  componentId: "sc-1y0c5re-21"
})(["font-size:12.5px;font-weight:600;color:#3d4a53;"]);
var EmptyHint = _styledComponents["default"].div.withConfig({
  displayName: "NotificationsIconmodern__EmptyHint",
  componentId: "sc-1y0c5re-22"
})(["margin-top:3px;font-size:12px;color:", ";"], (0, _Theme.val)("mutedColor"));
function MessageCard(_ref) {
  var notification = _ref.notification,
    index = _ref.index,
    readNotification = _ref.readNotification;
  var time = (0, _react.useContext)(_Time.TimeContext);
  var _useState = (0, _react.useState)(false),
    isOpen = _useState[0],
    setOpen = _useState[1];
  var timezone = time && time.timezone && time.timezone.name;
  var date = timezone ? (0, _momentTimezone["default"])(notification.timestamp).tz(timezone) : (0, _momentTimezone["default"])(notification.timestamp);
  var unread = !notification.read;
  return /*#__PURE__*/_react["default"].createElement(_react["default"].Fragment, null, /*#__PURE__*/_react["default"].createElement(Card, {
    "data-testid": "NotificationLine-" + notification.id,
    title: notification.name,
    onClick: function onClick() {
      readNotification(index);
      setOpen(true);
    }
  }, /*#__PURE__*/_react["default"].createElement(Edge, {
    $type: notification.type,
    $unread: unread
  }), /*#__PURE__*/_react["default"].createElement(TopLine, null, /*#__PURE__*/_react["default"].createElement(TypeChip, {
    $type: notification.type
  }, /*#__PURE__*/_react["default"].createElement(_core.Icon, {
    icon: typeIcon(notification.type),
    size: 12
  })), /*#__PURE__*/_react["default"].createElement(CardTitle, {
    $unread: unread
  }, notification.name), unread && /*#__PURE__*/_react["default"].createElement(Dot, {
    "data-testid": "NotificationLine-" + notification.id + ".dot"
  }), /*#__PURE__*/_react["default"].createElement(CardDate, {
    dateTime: notification.timestamp
  }, date.format(DATE_FORMAT))), /*#__PURE__*/_react["default"].createElement(CardMessage, null, notification.message)), isOpen && /*#__PURE__*/_react["default"].createElement(_NotificationPopupWidget.NotificationPopupWidget, {
    notification: notification,
    onClickNoButton: function onClickNoButton() {
      return setOpen(false);
    },
    onClickYesButton: function onClickYesButton() {
      readNotification(index);
      setOpen(false);
    }
  }));
}
var NotificationsIcon = exports.NotificationsIcon = function NotificationsIcon() {
  var _useContext = (0, _react.useContext)(_Core.TranslatorContext),
    translate = _useContext.translate;
  var notificationsIconContext = (0, _react.useContext)(_NotificationsIconContext.NotificationsIconContext) || {};
  var notifications = notificationsIconContext.notifications,
    readNotification = notificationsIconContext.readNotification,
    readAllNotifications = notificationsIconContext.readAllNotifications;
  var _useState2 = (0, _react.useState)(false),
    isOpen = _useState2[0],
    setOpen = _useState2[1];
  var _useState3 = (0, _react.useState)(FILTER_ALL),
    filter = _useState3[0],
    setFilter = _useState3[1];
  var all = (0, _react.useMemo)(function () {
    return notifications || [];
  }, [notifications]);
  var unread = (0, _react.useMemo)(function () {
    return all.filter(function (_ref2) {
      var read = _ref2.read;
      return !read;
    }).length;
  }, [all]);
  // Keep each message's index in the full list; that is what the context's
  // readNotification expects, whatever the filter shows.
  var shown = (0, _react.useMemo)(function () {
    return all.map(function (notification, index) {
      return {
        notification: notification,
        index: index
      };
    }).filter(function (_ref3) {
      var notification = _ref3.notification;
      return filter === FILTER_ALL || !notification.read;
    });
  }, [all, filter]);
  var title = translate("NotificationsIcon.toolbar.Notifications", "Messages");
  var renderSegment = function renderSegment(value, label, count, testId) {
    return /*#__PURE__*/_react["default"].createElement(Segment, {
      type: "button",
      role: "tab",
      "aria-selected": filter === value,
      $active: filter === value,
      "data-testid": testId,
      onClick: function onClick() {
        return setFilter(value);
      }
    }, label, count > 0 && /*#__PURE__*/_react["default"].createElement(SegmentCount, {
      "data-testid": value === FILTER_UNREAD ? "NotificationsIcon.Unread" : undefined
    }, count));
  };
  return /*#__PURE__*/_react["default"].createElement(_core.Popover, {
    position: _core.Position.BOTTOM_RIGHT,
    minimal: true
    // No arrow, but keep a small gap under the envelope.
    ,
    modifiers: {
      offset: {
        enabled: true,
        options: {
          offset: [0, 8]
        }
      }
    },
    canEscapeKeyClose: true,
    isOpen: isOpen,
    enforceFocus: false,
    onClose: function onClose() {
      return setOpen(false);
    },
    content: /*#__PURE__*/_react["default"].createElement(Panel, {
      "data-testid": "NotificationsIcon.Wrapper"
    }, /*#__PURE__*/_react["default"].createElement(Header, {
      "data-testid": "NotificationsIcon.Toolbar.Wrapper"
    }, /*#__PURE__*/_react["default"].createElement(TitleRow, null, /*#__PURE__*/_react["default"].createElement(Title, null, title), /*#__PURE__*/_react["default"].createElement(ReadAll, {
      type: "button",
      "data-testid": "NotificationsIcon.Toolbar.ReadAll",
      disabled: unread === 0,
      onClick: readAllNotifications
    }, translate("NotificationsIcon.Toolbar.ReadAll", "Mark all as read"))), /*#__PURE__*/_react["default"].createElement(Segments, {
      role: "tablist"
    }, renderSegment(FILTER_ALL, translate("NotificationsIcon.filter.all", "All"), all.length, "NotificationsIcon.Filter.All"), renderSegment(FILTER_UNREAD, translate("NotificationsIcon.filter.unread", "Unread"), unread, "NotificationsIcon.Filter.Unread"))), shown.length > 0 ? /*#__PURE__*/_react["default"].createElement(List, null, shown.map(function (_ref4) {
      var notification = _ref4.notification,
        index = _ref4.index;
      return /*#__PURE__*/_react["default"].createElement(MessageCard, {
        key: notification.id || index,
        notification: notification,
        index: index,
        readNotification: readNotification
      });
    })) : /*#__PURE__*/_react["default"].createElement(Empty, {
      "data-testid": "NotificationsIcon.Empty"
    }, /*#__PURE__*/_react["default"].createElement(EmptyIcon, null, /*#__PURE__*/_react["default"].createElement(_core.Icon, {
      icon: _icons.IconNames.ENVELOPE,
      size: 16
    })), /*#__PURE__*/_react["default"].createElement(EmptyTitle, null, filter === FILTER_UNREAD && all.length > 0 ? /*#__PURE__*/_react["default"].createElement(_reactIntl.FormattedMessage, {
      id: "NotificationsIcon.empty.unread",
      defaultMessage: "No unread messages"
    }) : /*#__PURE__*/_react["default"].createElement(_reactIntl.FormattedMessage, {
      id: "NotificationsPanel.label.NoNewMessages",
      defaultMessage: "No new messages"
    })), /*#__PURE__*/_react["default"].createElement(EmptyHint, null, /*#__PURE__*/_react["default"].createElement(_reactIntl.FormattedMessage, {
      id: "NotificationsIcon.empty.hint",
      defaultMessage: "You're all caught up"
    }))))
  }, /*#__PURE__*/_react["default"].createElement(Anchor, null, /*#__PURE__*/_react["default"].createElement(_core.Button, {
    "data-testid": "NotificationsIcon.Button",
    icon: _icons.IconNames.ENVELOPE,
    "aria-label": title,
    onClick: function onClick() {
      return setOpen(!isOpen);
    }
  }), unread > 0 && /*#__PURE__*/_react["default"].createElement(Badge, {
    "data-testid": "NotificationsIcon.NewMessage"
  }, unread > 99 ? "99+" : unread)));
};